import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CompletionRequest, CompletionMessage } from '@app-libs/model';
import { StreamingApiClient } from '../client/streaming-api-client';
import { HttpClient } from '@angular/common/http';
import { ConfigurationClientService } from '../client/configuration-client.service';

@Injectable({
  providedIn: 'root',
})
export class GeminiApiService extends StreamingApiClient {
  private readonly basePath = '/gemini'; // Gemini-specific endpoint

  constructor(
    protected override config: ConfigurationClientService,
    protected override http: HttpClient,
  ) {
    super(config, http);
  }

  override get baseUrl(): string {
    return this.config.getBackendAPIUrl() + this.basePath;
  }

  /**
   * Creates a completion using Google Gemini API.
   * Supports both streaming and non-streaming modes.
   * 
   * This provides direct access to Gemini provider for:
   * - Testing Gemini-specific functionality
   * - Comparing providers during development
   * - Backup option when unified endpoint has issues
   */
  createCompletion(request: CompletionRequest): Observable<any> {
    console.debug('GeminiApiService: Creating completion with request:', request);
    const url = `${this.baseUrl}/completion`;
    // Always streaming for consistency with other providers
    return this.createStreamingRequest(url, request);
  }

  /**
   * Creates a plain-text non-streaming completion using Gemini API.
   * Useful for simple text generation without streaming overhead.
   */
  createCompletionAsText(
    prompt: string,
    messages: Partial<CompletionMessage>[],
    model?: string,
  ): Observable<string> {
    const url = `${this.baseUrl}/completion/text`;
    return this.http.post(url, { 
      prompt, 
      messages, 
      model: model || 'gemini-1.5-flash' 
    }, { responseType: 'text' });
  }

  /**
   * Creates a completion with image support using Gemini's multimodal capabilities.
   * Supports images, PDFs, and other file types that Gemini can process.
   */
  createImageCompletion(
    prompt: string,
    messages: Partial<CompletionMessage>[],
    model: string,
    image: File,
  ): Observable<string> {
    const url = `${this.baseUrl}/completion/image`;
    const formData = new FormData();
    formData.append('prompt', prompt);
    formData.append('messages', JSON.stringify(messages));
    formData.append('model', model || 'gemini-1.5-flash');
    formData.append('image', image);
    
    return this.http.post(url, formData, { responseType: 'text' });
  }

  /**
   * Gets available Gemini models and their capabilities.
   * Note: This would need to be implemented on the backend.
   */
  getAvailableModels(): Observable<any> {
    const url = `${this.baseUrl}/models`;
    return this.http.get(url);
  }
}